import { EmptyState } from '@backstage/core-components';
import { EntityRefLink, useEntity } from '@backstage/plugin-catalog-react';
import {
  Card,
  CardContent,
  CardHeader,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import React, { FC, useState } from 'react';
import { useAppSelector } from '../../hooks/reduxhooks';
import { OwnershipDetailsProps, dhDocktype } from './types';
import { contentpages, notification } from '../../utils/constants';

enum toolTipTypes {
  component = `A component is an idividually,deployable piece of software, for example a library,
            mobile feature,web component,or backed service(list not exhaustive).
            A Component can be tracked in source control,or use some exisiting opensource or commercial software`,
  api = `Apis form an important(maybe the mosy important) abstraction that allows large software ecosystem to scale. Thus, APIs are the primary way to discover existing functionality in our ecosystem.`,
  resources = `Resources represents the infrastructure a software component needs to operate at runtime. This includes things like databases (e.g., BigTable), message queues (e.g., Pub/Sub), storage (e.g., S3 buckets), and content delivery networks (CDNs). By including resources in the Backstage Software Catalog, developers can visualize the entire resource footprint and create tools around them`,
  notification = `Notification`,
  onlineContent = `Online Content`,
}

export function parseDetails(rawString: string) {
  const parts = rawString?.split(', Spec: ');
  const nameDesc = parts[0]?.split(', Description: ');
  const titleDesc = parts[0]?.split(', Title: ');
  const parsedObject: OwnershipDetailsProps = {
    name: nameDesc[0]?.replace('Name: ', '').trim(),
    description: nameDesc[1]?.split(', Title:')[0].trim(),
    title: titleDesc[1],
    spec: JSON.parse(parts[1]),
  };
  return parsedObject;
}

const useCustomSystemStyles = makeStyles({
  customText: {
    wordBreak: 'break-word',
    width: '15em',
  },
  helpButton: {
    backgroundColor: '#05164d',
    color: 'white',
    border: 'none',
    borderRadius: '50%',
    width: '25px',
    height: '25px',
    fontSize: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    '&:hover': {
      backgroundColor: '#0a2a80',
    },
  },
});

const typeMap: Record<string, string> = {
  notification,
  'online content': contentpages,
};

const isCapabilityMatch = (spec: any, selectedEntityName: string) => {
  const capabilityRefs = [spec?.capabilities].flat().filter(Boolean) as string[];
  if (!capabilityRefs.length) {
    return false;
  }

  const normalizedSelected = selectedEntityName.toLowerCase();

  return capabilityRefs.some(capability => {
    const normalizedCapability = capability.toLowerCase();
    const capabilityName = normalizedCapability.includes('/')
      ? normalizedCapability.split('/').pop()
      : normalizedCapability.split(':').pop();

    return (
      normalizedCapability === normalizedSelected ||
      capabilityName === normalizedSelected
    );
  });
};

const CustomizedSystemPage: FC<{ type: dhDocktype }> = ({ type }) => {
  const classes = useCustomSystemStyles();
  const { entity } = useEntity();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const { owners } = useAppSelector(state => state.ownersProps);
  const ownersList = Array.isArray(owners) ? owners : [];

  const mainOwner = window?.location?.pathname?.split('/')[4]?.toLowerCase();
  let ownerDetails: any[] = [];
  ownersList
    .filter(x => {
      const entityOwners = entity.spec?.owner
        ?.toString()
        .toLowerCase()
        .split(' ')
        .map(item => item.trim());

      const ownerList = x?.owners
        ?.split(' ')
        .map((item: string) => item.trim().toLowerCase());

      if (x?.owners) {
        const parsedDetails_ = parseDetails(x?.details);

        if (
          parsedDetails_?.spec?.system?.toLowerCase() ===
            mainOwner?.toLowerCase() ||
          parsedDetails_.spec.products
            ?.toString()
            ?.toLowerCase()
            .includes(mainOwner.toLowerCase()) ||
          isCapabilityMatch(parsedDetails_?.spec, mainOwner)
        ) {
          ownerDetails.push(x);
        }
      }

      return (
        ownerList.includes(
          entity.spec?.owner?.toString().toLowerCase().trim(),
        ) || entityOwners!!.some(owner => ownerList.includes(owner))
      );
    })
    .filter(x =>
      x.kind === type.toLocaleLowerCase() && type === 'Component'
        ? x.component_count > 0
        : type === 'api'
          ? x.api_count > 0
          : x.resource_count > 0,
    );

  const handleChangePage = (event: any, newPage: number) => {
    event.preventDefault();
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = ownerDetails
    .filter(x => {
      const entityType = x.entity_ref.split(':')[0].toLowerCase();
      const parsed = parseDetails(x.details);
      const itemType = parsed?.spec?.type?.toLowerCase();

      // Tech Components tab (Component, API, Resource)
      if (type.toLowerCase() === 'component') {
        return (
          entityType === 'component' &&
          ![notification, contentpages].some(y => itemType?.includes(y))
        );
      }

      // Business Components tab (Notification, Online Content)
      if (
        type.toLowerCase() === notification ||
        type.toLowerCase() === 'online content'
      ) {
        const normalizedType =
          typeMap[type.toLowerCase()] || type.toLowerCase().replace(' ', '-');
        return entityType === 'component' && itemType === normalizedType;
      }

      // API, Resource tabs
      return entityType === type.toLowerCase();
    })
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  function HelpTooltip() {
    return (
      <Tooltip
        title={
          type === 'Component'
            ? toolTipTypes.component
            : type === 'Resource'
              ? toolTipTypes.resources
              : type === 'Notification'
                ? toolTipTypes.notification
                : type === 'Online Content'
                  ? toolTipTypes.onlineContent
                  : toolTipTypes.api
        }
        placement="top"
        arrow
      >
        <IconButton size="small">
          <button className={`${classes.helpButton}`}>?</button>
        </IconButton>
      </Tooltip>
    );
  }
  return (
    <Card data-testid="entity-has-systems-card">
      <CardHeader
        title={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              borderRadius: '50%',
            }}
          >
            {type === 'api' ? type.toUpperCase() : type.concat('s')}
            <HelpTooltip />
          </div>
        }
      />
      <CardContent>
        {paginatedData.length ? (
          <>
            <Table aria-label="group list" size="small">
              <TableHead>
                <TableRow>
                  <TableCell>NAME</TableCell>
                  <TableCell>OWNER</TableCell>
                  <TableCell>TYPE</TableCell>
                  <TableCell>LIFECYCLE</TableCell>
                  <TableCell>DESCRIPTION</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((row: any, i: number) => {
                  const parsedData = parseDetails(row.details);
                  return (
                    <TableRow key={row?.metadata?.name + i}>
                      <TableCell style={{ width: '30em' }}>
                        <EntityRefLink entityRef={row?.entity_ref}>
                          <b>
                            {parsedData?.title ||
                              parsedData?.name?.replaceAll('-', ' ')}
                          </b>
                        </EntityRefLink>
                      </TableCell>
                      <TableCell className={classes.customText}>
                        {parsedData?.spec?.owner?.includes(' ') ? (
                          parsedData.spec.owner
                            .split(' ')
                            .map((item: string, index: number) => (
                              <EntityRefLink
                                key={index}
                                entityRef={{
                                  kind: 'group',
                                  namespace: 'default',
                                  name: item,
                                }}
                              />
                            ))
                        ) : (
                          <EntityRefLink
                            entityRef={{
                              kind: 'group',
                              namespace: 'default',
                              name: parsedData?.spec?.owner,
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell className={classes.customText}>
                        {parsedData?.spec?.type}
                      </TableCell>
                      <TableCell className={classes.customText}>
                        {parsedData?.spec?.lifecycle}
                      </TableCell>
                      <TableCell
                        className={classes.customText}
                        style={{ width: '30em' }}
                      >
                        {parsedData?.description}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 100]}
              component="div"
              count={
                type === 'Notification'
                  ? ownerDetails?.filter(x => {
                      const parsed = parseDetails(x.details);
                      const itemType = parsed?.spec?.type?.toLowerCase();
                      return itemType === notification;
                    }).length
                  : type === 'Online Content'
                    ? ownerDetails.filter(x => {
                        const parsed = parseDetails(x.details);
                        const itemType = parsed?.spec?.type?.toLowerCase();
                        return itemType === contentpages;
                      }).length
                    : ownerDetails.filter(
                        x =>
                          x.entity_ref.split(':')[0].toLowerCase() ===
                          type.toLowerCase(),
                      ).length
              }
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        ) : (
          <EmptyState
            missing="data"
            title={`This Product has no ${type === 'api' ? type.toUpperCase() : type.concat('s')}`}
          />
        )}
      </CardContent>
    </Card>
  );
};
export default CustomizedSystemPage;
